import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../shared/services/firebase';
import { firestoreService } from '../../shared/services/firestoreService';
import { CheckIcon, XIcon, SearchIcon } from './icons/UiIcons';
import { checkinMessage } from '../../shared/checkinErrors';

interface ManualValidationProps {
    onClose: () => void;
}

type Step = 'search' | 'confirm' | 'success' | 'error';

interface FoundClient {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

const ManualValidation: React.FC<ManualValidationProps> = ({ onClose }) => {
    const [step, setStep] = useState<Step>('search');
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<FoundClient[]>([]);
    const [selectedClient, setSelectedClient] = useState<FoundClient | null>(null);
    const [reason, setReason] = useState('nfc_failure');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [gain, setGain] = useState('');

    const currentUser = firestoreService.getCurrentUser();
    const restaurantId = String(currentUser?.restaurantId ?? '');

    const searchClients = async () => {
        if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
        setLoading(true);
        setError('');
        try {
            const membershipsSnap = await getDocs(
                query(collection(db, 'memberships'), where('restaurantId', '==', restaurantId))
            );

            const clients: FoundClient[] = [];
            for (const memDoc of membershipsSnap.docs) {
                const userId = memDoc.data().userId;
                const userSnap = await getDoc(doc(db, 'users', userId));
                if (!userSnap.exists()) continue;
                const u = userSnap.data();
                const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
                const q = searchQuery.toLowerCase();
                if (fullName.includes(q) || (u.email ?? '').toLowerCase().includes(q) || (u.phone ?? '').includes(q)) {
                    clients.push({
                        id: userId,
                        firstName: u.firstName ?? '',
                        lastName: u.lastName ?? '',
                        email: u.email ?? '',
                    });
                }
            }
            setResults(clients);
            if (clients.length === 0) setError('Aucun client trouvé parmi vos inscrits');
        } catch (e: any) {
            setError(e.message || 'Erreur de recherche');
        } finally {
            setLoading(false);
        }
    };

    // Validation 100 % serveur (validateManualCheckin) : même verrou « 1 validation / jour »
    // que le tap NFC et le QR, même calcul des points, motif et auteur journalisés.
    const confirmValidation = async () => {
        if (!selectedClient || loading) return;
        setLoading(true);
        setError('');
        try {
            const res = await firestoreService.validateManualCheckin(selectedClient.id, reason);
            if (res.success) {
                setGain(res.programType === 'stamps' || (res.pointsEarned === 0 && (res.stampsEarned ?? 0) > 0)
                    ? `+${res.stampsEarned ?? 0} tampon${(res.stampsEarned ?? 0) > 1 ? 's' : ''}`
                    : `+${res.pointsEarned} pts`);
                setStep('success');
            } else {
                setError(checkinMessage(res.message ?? ''));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">

                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-slate-700">
                    <h2 className="text-lg font-black text-gray-900 dark:text-white">Validation manuelle</h2>
                    <button onClick={onClose} aria-label="Fermer" className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
                </div>

                {step === 'search' && (
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-500">Recherchez un client par nom ou email pour valider sa visite manuellement.</p>

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Nom, email ou téléphone..."
                                className="flex-1 border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-indigo-500 outline-none focus:ring-1 focus:ring-brand-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                onKeyDown={e => e.key === 'Enter' && searchClients()}
                            />
                            <button
                                onClick={searchClients}
                                disabled={loading || searchQuery.trim().length < 2}
                                aria-label="Rechercher"
                                className="px-4 py-3 bg-brand-primary text-white font-bold rounded-lg disabled:opacity-40 inline-flex items-center justify-center"
                            >
                                {loading ? '…' : <SearchIcon className="w-5 h-5" />}
                            </button>
                        </div>

                        {error && <p className="text-sm text-red-500 text-center">{error}</p>}

                        {results.length > 0 && (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {results.map(client => (
                                    <button
                                        key={client.id}
                                        onClick={() => { setSelectedClient(client); setStep('confirm'); }}
                                        className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-indigo-50 dark:hover:bg-slate-600 text-left transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                            <span className="text-indigo-700 font-bold">{client.firstName[0]?.toUpperCase()}</span>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white">{client.firstName} {client.lastName}</p>
                                            <p className="text-xs text-gray-400">{client.email}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {step === 'confirm' && selectedClient && (
                    <div className="p-6 space-y-4">
                        <div className="bg-indigo-50 dark:bg-slate-700 rounded-2xl p-4 text-center">
                            <p className="text-sm text-gray-500">Valider la visite de</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedClient.firstName} {selectedClient.lastName}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Raison</label>
                            <select
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            >
                                <option value="nfc_failure">Panne NFC / réseau</option>
                                <option value="client_phone_issue">Problème téléphone client</option>
                                <option value="confirmed_payment">Paiement confirmé au comptoir</option>
                                <option value="other">Autre</option>
                            </select>
                        </div>

                        {error && <p className="text-sm text-red-500 text-center">{error}</p>}

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setStep('search'); setError(''); }}
                                className="px-6 py-3 text-gray-500 hover:text-gray-700"
                            >
                                Retour
                            </button>
                            <button
                                onClick={confirmValidation}
                                disabled={loading}
                                className="flex-1 py-3 bg-green-600 text-white font-bold rounded-lg disabled:opacity-50"
                            >
                                {loading ? 'Validation…' : 'Valider la visite'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'success' && selectedClient && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <CheckIcon className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-extrabold text-green-600">Visite validée !</h3>
                        <p className="text-gray-700 dark:text-gray-300 font-semibold">{selectedClient.firstName} {selectedClient.lastName}</p>
                        <div className="bg-green-50 rounded-2xl px-6 py-4">
                            <p className="text-3xl text-emerald-600 dark:text-emerald-400 font-stat">{gain}</p>
                            <p className="text-xs text-gray-500">crédités (validation manuelle)</p>
                        </div>
                        <p className="text-xs text-gray-400">La validation et son motif sont enregistrés dans le journal du restaurant.</p>
                        <button onClick={onClose} className="w-full py-3 bg-indigo-600 text-white font-bold rounded-lg">Fermer</button>
                    </div>
                )}

                {step === 'error' && (
                    <div className="p-8 text-center space-y-4">
                        <div className="w-20 h-20 mx-auto rounded-full bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                            <XIcon className="w-10 h-10" />
                        </div>
                        <h3 className="text-xl font-bold text-red-600">Erreur</h3>
                        <p className="text-sm text-gray-500">{error}</p>
                        <button onClick={() => { setStep('search'); setError(''); }} className="w-full py-3 bg-gray-200 text-gray-800 font-bold rounded-2xl">Réessayer</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManualValidation;
